<template>
  <div class="fitslider" tabindex="0">
    <p class="show-for-sr" data-testid="fitslider-label-for-sr">
      On average, customers rate the {{ titleFormatted }} of this item as {{ closestLabel }}.
    </p>
    <div aria-hidden="true" data-testid="fitslider-main-container">
      <h5 class="margin-bottom-xxs heavy">
        {{ titleFormatted }}
      </h5>
      <div class="line">
        <div data-testid="fitslider-pointer" class="pointer" :style="`left: ${pointerPosition}`"></div>
      </div>
      <div class="grid-x grid-margin-x align-justify margin-top-xxs">
        <span data-testid="fitslider-minLabel" :class="`cell small-${midLabel ? 4 : 6}`">{{ minLabel }}</span>
        <span v-if="midLabel" data-testid="fitslider-midLabel" class="cell small-4 grid-x align-center text-center">{{ midLabel }}</span>
        <span data-testid="fitslider-maxLabel" :class="`cell small-${midLabel ? 4 : 6} grid-x align-right text-right`">{{ maxLabel }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useBrand } from '~/composables/useBrand';
import { reviewsPercentage } from '~/utils/commonUtils';
import { capitalizeOnlyFirstWord } from '~/utils/capitalize';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  labels: {
    type: Object,
    required: true,
    validator: (obj) => ['minLabel', 'midLabel', 'maxLabel'].every((x) => x in obj),
  },
  value: {
    type: Number,
    required: true,
  },
  valueRange: {
    type: Number,
    required: true,
  },
});
const pointerWidth = useBrand().isMcom ? 12 : 4;
const titleFormatted = capitalizeOnlyFirstWord(props.title);
const { minLabel, midLabel, maxLabel } = {
  minLabel: computed(() => capitalizeOnlyFirstWord(props.labels.minLabel)),
  midLabel: computed(() => capitalizeOnlyFirstWord(props.labels.midLabel || '')),
  maxLabel: computed(() => capitalizeOnlyFirstWord(props.labels.maxLabel)),
};

const percentage = reviewsPercentage(props.value, props.valueRange);

const pointerPosition = computed(() => (percentage === 0 ? '0%' : `calc(${percentage}% - ${pointerWidth / 2}px)`));

const ONE_THIRD = 100 / 3;
const TWO_THIRDS = 2 * ONE_THIRD;

// For screen readers
const closestLabel = computed(() => {
  let label;
  if (percentage >= 0 && percentage <= ONE_THIRD) {
    label = minLabel.value;
  }
  if (percentage > ONE_THIRD && percentage <= TWO_THIRDS) {
    if (midLabel.value) {
      label = midLabel.value;
    } else {
      label = (percentage >= 50 ? maxLabel : minLabel).value;
    }
  }
  if (percentage > TWO_THIRDS && percentage <= 100) {
    label = maxLabel.value;
  }
  return label;
});
</script>

<style scoped lang="scss">
.line {
  position: relative;
  width: 100%;
  height: rem-calc(8);
  background-color: $gray-5-color;

  .pointer {
    position: absolute;
    background-color: $black;
    top: rem-calc(-2);
  }
}
</style>

<style scoped lang="scss" brand="mcom">
.line {
  border-radius: rem-calc(8);

  .pointer {
    width: rem-calc(12);
    height: rem-calc(12);
    border-radius: rem-calc(8);
  }
}
</style>

<style scoped lang="scss" brand="bcom">
.pointer {
  left: 50%;
  width: rem-calc(4);
  height: rem-calc(12);
}
</style>
